import { Observable, throwError, Observer } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import {
  HttpClient,
  HttpHeaders,
  HttpResponse,
  HttpParams,
} from '@angular/common/http';

import { HttpAuthenticationProvider } from './http-authentication-provider';
import { HttpRequestErrorHandler } from './http-request-error-handler';
import { HttpRequest } from './http-request';
import { HttpEndpoint } from './http-endpoint';
import { HttpResponseContent } from './http-response-content.enum';
import { HttpError } from './http-error';
import { Injectable, Optional } from '@angular/core';
import { CustomerPortalProvider } from './http-customer-portal.provider';

@Injectable()
export class HttpRequestClient {
  constructor(
    private _httpClient: HttpClient,
    private _httpEndpoint: HttpEndpoint,
    @Optional() public authProvider?: CustomerPortalProvider,
    @Optional() public errorHandler?: HttpRequestErrorHandler
  ) {}

  public call<R>(
    request: HttpRequest,
    noAuth: boolean = false
  ): Observable<HttpResponse<R>> {
    return this._call(request, false) as Observable<HttpResponse<R>>;
  }

  public callObject<R>(request: HttpRequest): Observable<R> {
    return this._call<R>(request, true) as Observable<R>;
  }

  private _call<R>(
    request: HttpRequest,
    objectResponse: boolean,
    retryCall: boolean = false
  ): Observable<R | HttpResponse<R> | null> {
    const observable: Observable<R | HttpResponse<R>> = this._httpClient
      .request<HttpResponse<R>>(
        request.getMethod().toString(),
        this.composeUrl(request),
        this.createRequest(request)
      )
      .pipe(
        catchError<R | HttpResponse<R>, Observable<R | HttpResponse<R>>>(
          (error: any, _: Observable<R | HttpResponse<R>>) => {
            const Error = new HttpError(error);

            if (this.isUnauthenticated(error) && !retryCall) {
              return this.onUnathenticated(
                request,
                observable as Observable<HttpResponse<any>>,
                Error,
                objectResponse
              );
            } else if (this.isForbiden(error)) {
              this.onAccessForbidden(
                request,
                observable as Observable<HttpResponse<any>>
              );
              return throwError(this.onError(Error));
            } else if (this.isRemoteServiceUnavailable(error)) {
              this.onRemoteServiceUnavailable(Error);
              return throwError(this.onError(Error));
            }

            return throwError(this.onError(Error));
          }
        )
      );

    if (objectResponse) {
      return observable.pipe(
        map((response) => (response as HttpResponse<R>).body)
      );
    }

    return observable;
  }

  private composeUrl(request: HttpRequest): string {
    return `${this._httpEndpoint.getRoot(request)}${request.getUrl()}`;
  }

  private isUnauthenticated(error: any) {
    return error.status === 401;
  }

  private isForbiden(error: any) {
    return error.status === 403;
  }

  private isRemoteServiceUnavailable(error: any) {
    return error.status === 502 || error.status === 504;
  }

  private onAccessForbidden(
    request: HttpRequest,
    response: Observable<HttpResponse<any>>
  ): Observable<HttpResponse<any>> | undefined {
    if (this.authProvider) {
      return this.authProvider.onAccessForbidden(this, request, response);
    }
    return undefined;
  }

  private onUnathenticated<R>(
    req: HttpRequest,
    res: Observable<HttpResponse<any>>,
    err: HttpError,
    objectResponse: boolean
  ): Observable<R | HttpResponse<R>> {
    if (req.isAuthenticated() && this.authProvider) {
      return Observable.create((obsrv: Observer<R | HttpResponse<R>>) => {
        this.authProvider!.onUnathenticated(this, req, res, err).subscribe(
          (success) => {
            if (success) {
              this.authenticateRequest(req);
              this._call(req, false, true).subscribe((response: any) => {
                obsrv.next(response);
                obsrv.complete();
              });
            } else {
              obsrv.error(new HttpError(null));
              obsrv.complete();
            }
          }
        );
      });
    } else {
      return throwError(this.onError(err));
    }
  }

  private onRemoteServiceUnavailable(error: any) {
    if (this.errorHandler) {
      this.errorHandler.onRemoteServiceUnavailable(error);
    }
  }

  private onError(error: HttpError): HttpError {
    if (this.errorHandler) {
      return this.errorHandler.onError(error);
    }

    return error;
  }

  public createRequest(request: HttpRequest): object {
    this.authenticateRequest(request);

    if (request.getContentType() != null) {
      request.header('Content-Type', request.getContentType()!.valueOf());
    }

    if (request.getAcceptType() != null) {
      request.header('Accept', request.getAcceptType()!.valueOf());
    }

    const requestOptions = {
      headers: new HttpHeaders(),
      observe: 'response',
      params: new HttpParams(),
      reportProgress: true,
      responseType: HttpResponseContent.Json.toString(),
      body: undefined,
    };

    if (request.getBody() != null) {
      requestOptions['body'] = request.getBody();
    }

    if (request.getResponseType()) {
      requestOptions.responseType = request.getResponseType();
    }

    request.getHeaders().forEach((header) => {
      requestOptions.headers = requestOptions.headers.append(
        header[0],
        header[1]
      );
    });

    request.getQueryParameters().forEach((queryElement) => {
      requestOptions.params = requestOptions.params.set(
        queryElement.key,
        queryElement.value
      );
    });

    // for stupid IE stuff
    // requestOptions.headers = requestOptions.headers.set('Cache-Control', 'no-cache');
    // requestOptions.headers = requestOptions.headers.set('Pragma', 'no-cache');

    return requestOptions;
  }

  private authenticateRequest(request: HttpRequest) {
    if (request.isAuthenticated() && this.authProvider) {
      this.authProvider.authenticate(request);
    }
  }
}
